import { useClubLogo } from '../lib/useClubLogo';
import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getImageUrl } from '../lib/api';

const FALLBACK_BG = '';


function IceParticles() {
  const particles = useMemo(() => {
    return Array.from({ length: 25 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 10}s`,
      duration: `${8 + Math.random() * 12}s`,
      size: `${2 + Math.random() * 4}px`,
    }));
  }, []);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <div key={p.id} className="ice-particle" style={{ left: p.left, bottom: '-10px', width: p.size, height: p.size, animationDelay: p.delay, animationDuration: p.duration }} />
      ))}
    </div>
  );
}

export default function Hero() {
  const LOGO_URL = useClubLogo();
  const [heroBg, setHeroBg] = useState(FALLBACK_BG);

  useEffect(() => {
    const bgUrl = getImageUrl('/api/public/hero-bg');
    const img = new Image();
    img.onload = () => setHeroBg(bgUrl);
    img.src = bgUrl;
  }, []);

  const scrollToGames = () => {
    const el = document.getElementById('naechstes-spiel');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="home-hero-section">
      <div className="absolute inset-0">
        <img src={heroBg} alt="Eishockey Arena" className="w-full h-full object-cover" style={{ opacity: 0.25 }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(0,0%,5%)] via-[hsl(0,0%,5%)]/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[hsl(0,0%,5%)]" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(1200px 600px at 20% 30%, rgba(70,199,255,0.06) 0%, transparent 60%), radial-gradient(900px 500px at 80% 10%, rgba(204,0,0,0.05) 0%, transparent 55%)' }} />
      </div>
      <IceParticles />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 w-full">
        {/* Mobile logo - shown above text on mobile */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="lg:hidden flex justify-center mb-8"
        >
          <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden bg-[hsl(0,0%,10%)] p-1">
            <img src={LOGO_URL} alt="EHV Dresden Devils" className="w-full h-full object-contain rounded-full logo-glow" style={{ background: '#141414' }} />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
          {/* Left: Copy (3 cols) */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: 'easeOut' }} className="lg:col-span-3 text-center lg:text-left">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-8">
              <div className="w-2 h-2 rounded-full bg-[#CC0000] animate-pulse" />
              <span className="text-[11px] text-white/60 uppercase tracking-[0.15em] font-medium">Seit 1997 • Dresden</span>
            </motion.div>

            <h1 className="text-4xl sm:text-5xl lg:text-7xl xl:text-[5.5rem] font-bold tracking-[-0.02em] leading-[0.9] mb-2" style={{ fontFamily: 'Space Grotesk' }}>
              <span className="text-white">EHV</span>
            </h1>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl xl:text-[5.5rem] font-bold tracking-[-0.02em] leading-[0.9] mb-2" style={{ fontFamily: 'Space Grotesk' }}>
              <span className="text-white">DRESDEN</span>
            </h1>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl xl:text-[5.5rem] font-bold tracking-[-0.02em] leading-[0.9] mb-8" style={{ fontFamily: 'Space Grotesk' }}>
              <span className="text-[#CC0000]">DEVILS</span>
            </h1>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.8 }}
              className="text-base sm:text-lg lg:text-xl text-white/40 mb-10 max-w-md mx-auto lg:mx-0 leading-relaxed italic">
              "Leidenschaft auf dem Eis,<br />Gemeinschaft im Verein!"
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
              className="flex flex-wrap gap-4 justify-center lg:justify-start">
              <button onClick={scrollToGames}
                className="group flex items-center gap-2 px-6 sm:px-7 py-3 sm:py-3.5 bg-[#CC0000] text-white font-bold rounded-lg hover:bg-[#B30000] active:scale-[0.98] transition-colors duration-200 text-sm uppercase tracking-wider"
                data-testid="home-hero-primary-cta">
                <Play className="w-4 h-4 fill-current" /> Nächstes Spiel
              </button>
              <Link to="/kader"
                className="px-6 sm:px-7 py-3 sm:py-3.5 bg-white/5 text-white border border-white/15 font-bold rounded-lg hover:bg-white/10 active:scale-[0.98] transition-colors duration-200 text-sm uppercase tracking-wider"
                data-testid="home-hero-secondary-cta">
                Kader ansehen
              </Link>
            </motion.div>
          </motion.div>

          {/* Right: Big Logo (desktop only) */}
          <motion.div initial={{ opacity: 0, scale: 0.8, rotate: -5 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ delay: 0.3, duration: 1, ease: 'easeOut' }}
            className="lg:col-span-2 hidden lg:flex items-center justify-center relative">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-[#CC0000]/15 blur-[80px] scale-[1.8]" />
              <div className="absolute inset-0 rounded-full bg-[#46C7FF]/5 blur-[60px] scale-[1.5]" />
              <div className="w-72 h-72 xl:w-[22rem] xl:h-[22rem] rounded-full overflow-hidden bg-[hsl(0,0%,10%)] p-2">
                <img src={LOGO_URL} alt="EHV Dresden Devils" className="relative w-full h-full object-contain rounded-full logo-glow drop-shadow-2xl" style={{ background: '#141414' }} />
              </div>
              <div className="absolute inset-[-20px] rounded-full border border-[#CC0000]/10 animate-pulse" style={{ animationDuration: '4s' }} />
              <div className="absolute inset-[-40px] rounded-full border border-white/5" />
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.8 }} className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <button onClick={scrollToGames} className="text-white/20 hover:text-white/40 transition-colors duration-200">
          <ChevronDown className="w-6 h-6 animate-bounce" />
        </button>
      </motion.div>
    </section>
  );
}
